import { UserPresence, UserStatus } from '@/types/presence';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Calculate a human-readable "last active" text from a UserPresence object
 * Returns: "En ligne", "Vu il y a X min/heures/jours", or "Hors ligne"
 */
export const getLastActiveText = (presence: UserPresence | null): string => {
  if (!presence) return 'Hors ligne';
  
  // User is currently active
  if (presence.is_active) return 'En ligne';
  
  // No last_seen data available
  if (!presence.last_seen) return 'Hors ligne';
  
  const lastSeen = new Date(presence.last_seen);
  const now = Date.now();
  const diffMs = now - lastSeen.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  // Less than 1 minute
  if (diffMinutes < 1) return 'À l\'instant';
  
  // Less than 1 hour
  if (diffMinutes < 60) return `Vu il y a ${diffMinutes} min`;
  
  // Less than 24 hours
  if (diffHours < 24) return `Vu il y a ${diffHours}h`;
  
  // Less than 7 days
  if (diffDays < 7) return `Vu il y a ${diffDays}j`;
  
  // More than 7 days - use date-fns for formatting
  return formatDistanceToNow(lastSeen, { 
    addSuffix: true, 
    locale: fr 
  });
};

/**
 * Get realistic user status by checking activity_timeout
 * Returns 'offline' if timeout is expired, otherwise returns the declared status
 */
export const getRealisticUserStatus = (presence: UserPresence | null): UserStatus => {
  if (!presence) return 'offline';
  
  // Si pas de timeout défini, considérer offline
  if (!presence.activity_timeout) return 'offline';
  
  // Vérifier si le timeout est expiré
  const timeoutDate = new Date(presence.activity_timeout);
  const now = new Date();
  
  if (timeoutDate < now) {
    // Timeout expiré = vraiment offline
    return 'offline';
  }
  
  // Timeout valide = utiliser le statut déclaré
  return presence.status;
};
